/* ==========================================================================
   ux-candyrack-cart — the pieces of the Tortuga cart drawer design
   (Figma 3262:9044) that the Candy Rack slide cart has no setting for.

   The slide cart is the Candy Rack app embed, a client-rendered cart in
   #candyrack-slider-cart-root that re-renders on every cart change. This
   watches that root and, after each render, puts back:

   - the line-item savings pill: the app's own discount line stays in place
     (CSS hides its "TITLE (-$20)" text) and gets a "You saved $20 by
     bundling" sentence beside it, read from the app's amount;
   - the line's crossed-out compare-at price: the app prints only the price
     it charges, and /cart.js carries no compare-at price, so it is read
     from the product's /products/{handle}.js (once per handle) for the
     variant the line's title links to, times the line's quantity. A line
     the app already crosses out (its own discount) is left to the app;
   - the order savings line under the subtotal: original total less total,
     from /cart.js, fetched only when the subtotal the app shows changes;
   - the trust row under the check out button, cloned from the
     <template> snippets/ux-candyrack-cart.liquid prints.

   Every addition is a node of ours appended beside the app's nodes, never a
   change to a node the app renders, so its next render cannot trip on them;
   each pass only writes what differs, so the observer settles.

   Loaded once, from snippets/ux-candyrack-cart.liquid.
   ========================================================================== */

(function () {
  'use strict';

  const ROOT_ID = 'candyrack-slider-cart-root';
  const TRUST_TEMPLATE_ID = 'UxCandyrackTrust';

  const selectors = {
    cart: '#candyrack-slider-cart',
    discount: '.candyrack-slider-cart-item-product-details-discount',
    discountAmount: '.candyrack-slider-cart-item-product-details-discount-amount',
    item: '.candyrack-slider-cart-item-container',
    itemTitle: '.candyrack-slider-cart-item-product-details-title',
    quantity: '.candyrack-slider-cart-item-product-details-quantity-input',
    priceContainer: '.candyrack-slider-cart-item-product-details-price-container',
    appCompare: '.candyrack-slider-cart-item-product-details-discounted-price',
    compare: '.ux-cr-compare',
    subtotalSection: '.candyrack-slider-cart-footer-subtotal-discounts-section',
    subtotalPrice: '.candyrack-slider-cart-footer-subtotal-price',
    footer: '.candyrack-slider-cart-footer-wrapper',
    buttons: '.candyrack-slider-cart-footer-buttons-wrapper',
    saved: '.ux-cr-saved',
    orderSaved: '.ux-cr-order-saved',
    trust: '.ux-cr-trust',
  };

  const strings = window.uxCandyrackCart || {};
  const savedLabel = strings.saved || 'You saved [amount]';
  const savedBundleLabel = strings.savedBundle || 'You saved [amount] by bundling';
  const orderSavedLabel = strings.orderSaved || 'You saved [amount] on this order';

  let observer = null;
  let scheduled = false;
  let lastSubtotal = null;
  let orderSavedText = '';

  /* handle → {variantId: compare-at cents, 0 when not on sale}, filled as
     each product's JSON lands; a handle in `comparePending` is in flight. */
  const comparePrices = new Map();
  const comparePending = new Set();

  function setText(element, text) {
    if (element.textContent !== text) element.textContent = text;
  }

  function formatMoney(cents) {
    if (window.theme && typeof window.theme.formatMoney === 'function') {
      return window.theme.formatMoney(cents, window.theme.moneyFormat);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  /* "(-$20)" → "$20" */
  function cleanAmount(text) {
    return text
      .trim()
      .replace(/^\(\s*/, '')
      .replace(/\s*\)$/, '')
      .replace(/^[-−]\s*/, '');
  }

  function renderLineSavings(cart) {
    cart.querySelectorAll(selectors.discount).forEach((discount) => {
      const amount = discount.querySelector(selectors.discountAmount);
      if (!amount) return;

      const title = Array.from(discount.children)
        .filter((child) => child !== amount && !child.matches(selectors.saved))
        .map((child) => child.textContent)
        .join(' ');
      const label = /bundl/i.test(title) ? savedBundleLabel : savedLabel;
      const text = label.replace('[amount]', cleanAmount(amount.textContent));

      let saved = discount.querySelector(selectors.saved);
      if (!saved) {
        saved = document.createElement('span');
        saved.className = 'ux-cr-saved';
        discount.appendChild(saved);
      }
      setText(saved, text);
      if (!discount.hasAttribute('data-ux-saved')) discount.setAttribute('data-ux-saved', '');
    });
  }

  function rootPath() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function loadComparePrices(handle) {
    if (comparePrices.has(handle)) return comparePrices.get(handle);
    if (comparePending.has(handle)) return null;
    comparePending.add(handle);

    fetch(`${rootPath()}products/${encodeURIComponent(handle)}.js`, {
      headers: {Accept: 'application/json'},
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null)
      .then((product) => {
        const prices = {};
        ((product && product.variants) || []).forEach((variant) => {
          prices[variant.id] = variant.compare_at_price > variant.price ? variant.compare_at_price : 0;
        });
        comparePending.delete(handle);
        comparePrices.set(handle, prices);
        schedule();
      });
    return null;
  }

  /* "/fr/products/pro-40l?variant=123" → the crossed-out line total, or ''. */
  function comparePriceText(item) {
    const link = item.querySelector(selectors.itemTitle);
    const input = item.querySelector(selectors.quantity);
    if (!link || !input) return '';

    const url = new URL(link.getAttribute('href') || '', window.location.origin);
    const handle = (url.pathname.split('/products/')[1] || '').split('/')[0];
    const variantId = url.searchParams.get('variant');
    if (!handle || !variantId) return '';

    const prices = loadComparePrices(decodeURIComponent(handle));
    const compareAt = prices && prices[variantId];
    if (!compareAt) return '';

    const quantity = parseInt(input.value || input.getAttribute('data-value'), 10) || 1;
    return formatMoney(compareAt * quantity);
  }

  function renderComparePrices(cart) {
    cart.querySelectorAll(selectors.item).forEach((item) => {
      const container = item.querySelector(selectors.priceContainer);
      if (!container) return;

      const text = container.querySelector(selectors.appCompare) ? '' : comparePriceText(item);
      let compare = container.querySelector(selectors.compare);
      if (!compare) {
        if (!text) return;
        compare = document.createElement('s');
        compare.className = 'ux-cr-compare';
        container.appendChild(compare);
      }
      setText(compare, text);
      if (compare.hidden !== !text) compare.hidden = !text;
    });
  }

  function renderOrderSavings(cart) {
    const section = cart.querySelector(selectors.subtotalSection);
    if (!section) return;

    let line = section.querySelector(selectors.orderSaved);
    if (!line) {
      line = document.createElement('p');
      line.className = 'ux-cr-order-saved';
      section.appendChild(line);
    }
    setText(line, orderSavedText);
    line.hidden = !orderSavedText;

    const subtotal = cart.querySelector(selectors.subtotalPrice);
    const subtotalText = subtotal ? subtotal.textContent : '';
    if (subtotalText === lastSubtotal) return;
    lastSubtotal = subtotalText;

    fetch(`${rootPath()}cart.js`, {
      headers: {Accept: 'application/json'},
    })
      .then((response) => response.json())
      .then((data) => {
        const savings = data.original_total_price - data.total_price;
        orderSavedText = savings > 0 ? orderSavedLabel.replace('[amount]', formatMoney(savings)) : '';
        schedule();
      })
      .catch(() => {});
  }

  function renderTrust(cart) {
    const footer = cart.querySelector(selectors.footer);
    const template = document.getElementById(TRUST_TEMPLATE_ID);
    if (!footer || !template || footer.querySelector(selectors.trust)) return;

    const buttons = footer.querySelector(selectors.buttons);
    const trust = template.content.firstElementChild.cloneNode(true);
    if (buttons && buttons.nextSibling) {
      footer.insertBefore(trust, buttons.nextSibling);
    } else {
      footer.appendChild(trust);
    }
  }

  function render() {
    scheduled = false;
    const root = document.getElementById(ROOT_ID);
    const cart = root && root.querySelector(selectors.cart);
    if (!cart) return;

    renderLineSavings(cart);
    renderComparePrices(cart);
    renderOrderSavings(cart);
    renderTrust(cart);
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    /* A microtask, not a frame: rAF stalls in a hidden tab, and a cart
       opened there would keep the app's bare render until shown. */
    window.queueMicrotask(render);
  }

  function watch(root) {
    observer = new MutationObserver(schedule);
    observer.observe(root, {childList: true, subtree: true, characterData: true});
    schedule();
  }

  function init() {
    const root = document.getElementById(ROOT_ID);
    if (root) {
      watch(root);
      return;
    }

    /* The app embed mounts its root after load; wait for it on <body>. */
    const waiting = new MutationObserver(() => {
      const mounted = document.getElementById(ROOT_ID);
      if (!mounted) return;
      waiting.disconnect();
      watch(mounted);
    });
    waiting.observe(document.body, {childList: true});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
